/**
 * Regions (see stream_plan.md, "Segments"): the things a sphere is
 * simulated in, one Durable Object (or one GameServer in Node) each. Two
 * kinds: the grid areas of areas.ts, fixed squares, and the streams'
 * segments, each a stretch of one stream's belt that moves round its track
 * with its rocks. A segment's footprint at time `t` follows from the route
 * and the clock, so every region works out who overlaps whom without being
 * told.
 *
 * Keys: "x,y" for an area, "s:i:k" for segment k of stream i. Durable
 * Object names: "<sphere>:x:y" and "<sphere>:s:i:k".
 */
import { STREAM } from "../constants";
import type { Vec2 } from "../math";
import { AREA, areaBounds, areaExists, areaKey, areaOf, areasNear, distanceOutside, neighbourAreas, parseAreaKey, sameArea, type AreaCoord, type Bounds, type SphereConfig } from "./areas";
import { generateGlobal, startPoint, startRock } from "./generate";
import { segmentOf, streamPoint, type StreamRoute } from "./stream";

/** The region a stream rock is owned by: its segment's of its stream (see segmentOf). Null for a rock that isn't a stream's. */
export function rockRegion(sphere: SphereConfig, def: { drift?: { stream?: { id: number; u: number } } }): Region | null {
  const st = def.drift?.stream;
  return st ? segmentRegion(st.id, segmentOf(generateGlobal(sphere).streams[st.id]!, st.u)) : null;
}

/** Where new players start at `nowMs` (see startRock): the start rock's segment, or the area of the sphere's middle without a stream. */
export function startRegion(sphere: SphereConfig, nowMs: number): Region {
  const def = startRock(sphere, nowMs);
  return (def && rockRegion(sphere, def)) ?? { kind: "area", area: areaOf(sphere, startPoint(sphere, nowMs)) };
}

/** An area, or segment `k` of stream `stream`. */
export type Region = { kind: "area"; area: AreaCoord } | { kind: "segment"; stream: number; k: number };

export const areaRegion = (area: AreaCoord): Region => ({ kind: "area", area });
export const segmentRegion = (stream: number, k: number): Region => ({ kind: "segment", stream, k });

/** A region's short name within its sphere: "2,-1" or "s:1:14". */
export function regionKey(r: Region): string {
  return r.kind === "area" ? areaKey(r.area) : `s:${r.stream}:${r.k}`;
}

/** The region a short name stands for. */
export function parseRegionKey(key: string): Region | null {
  const s = /^s:(\d+):(\d+)$/.exec(key);
  if (s) return segmentRegion(Number(s[1]), Number(s[2]));
  if (key.startsWith("s:")) return null;
  const a = parseAreaKey(key);
  return a ? { kind: "area", area: a } : null;
}

/** The name a region's Durable Object goes by: "<sphere>:<x>:<y>" or "<sphere>:s:<stream>:<k>". */
export function regionName(sphere: string, r: Region): string {
  return r.kind === "area" ? `${sphere}:${r.area.x}:${r.area.y}` : `${sphere}:s:${r.stream}:${r.k}`;
}

/** The region a Durable Object name stands for ("public:2:-1", "public:s:1:14"), and the sphere's id. */
export function parseRegionName(name: string): { sphere: string; region: Region } | null {
  const parts = name.split(":");
  if (parts.length === 4 && parts[1] === "s") return { sphere: parts[0]!, region: segmentRegion(Number(parts[2]), Number(parts[3])) };
  if (parts.length === 3 && parts[1] !== "s") return { sphere: parts[0]!, region: { kind: "area", area: { x: Number(parts[1]), y: Number(parts[2]) } } };
  return null;
}

export const sameRegion = (a: Region, b: Region): boolean =>
  a.kind === "area" && b.kind === "area" ? sameArea(a.area, b.area) : a.kind === "segment" && b.kind === "segment" && a.stream === b.stream && a.k === b.k;

/** Does the region exist in the sphere: an area inside the shell, a segment one of its streams' belts has? */
export function regionExists(sphere: SphereConfig, r: Region): boolean {
  if (r.kind === "area") return areaExists(sphere, r.area);
  const route = Number.isInteger(r.stream) ? generateGlobal(sphere).streams[r.stream] : undefined;
  return !!route && Number.isInteger(r.k) && r.k >= 0 && r.k < route.segments;
}

/** How far past a segment's belt stretch its footprint reaches: half the band, the biggest rock's grid (any way round), and a knock. */
const SEGMENT_PAD = STREAM.band / 2 + (Math.max(STREAM.namedRadius[1], STREAM.large.radius[1]) * 1.25 + STREAM.bigPad) * 1.42 + 32;

const footprints = new Map<string, { at: number; bounds: Bounds }>();

/** What segment `k` of stream `stream` covers at time `t` (s): a box round its stretch of belt and the rocks on it. Cached for a few seconds (it moves 12 blocks a second). */
export function segmentBounds(sphere: SphereConfig, stream: number, k: number, t: number): Bounds {
  const route = generateGlobal(sphere).streams[stream]!;
  const key = `${sphere.seed}:${sphere.radius}:${sphere.areaSize}:${stream}:${k}`;
  const had = footprints.get(key);
  if (had && Math.abs(t - had.at) < 4) return had.bounds;
  const bounds = stretchBounds(route, k * route.segmentLength + STREAM.speed * t, route.segmentLength, SEGMENT_PAD + STREAM.speed * 4);
  footprints.set(key, { at: t, bounds });
  return bounds;
}

/** A box round the track from `s0` for `len` blocks, padded by `pad`. */
export function stretchBounds(route: Pick<StreamRoute, "pieces" | "length">, s0: number, len: number, pad: number): Bounds {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const step = 40;
  for (let s = 0; s <= len + step; s += step) {
    const { p } = streamPoint(route, s0 + Math.min(s, len));
    if (p.x < x0) x0 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.x > x1) x1 = p.x;
    if (p.y > y1) y1 = p.y;
  }
  return { x0: x0 - pad, y0: y0 - pad, x1: x1 + pad, y1: y1 + pad };
}

/** What a region covers at time `t`: an area's square, or a segment's footprint now. */
export function regionBounds(sphere: SphereConfig, r: Region, t: number): Bounds {
  return r.kind === "area" ? areaBounds(sphere, r.area) : segmentBounds(sphere, r.stream, r.k, t);
}

/** Every segment (of every stream) whose footprint at `t` is within `reach` of `p`. */
export function segmentsNear(sphere: SphereConfig, p: Vec2, reach: number, t: number): Region[] {
  const out: Region[] = [];
  for (const route of generateGlobal(sphere).streams) {
    for (let k = 0; k < route.segments; k++) if (distanceOutside(segmentBounds(sphere, route.id, k, t), p) < reach) out.push(segmentRegion(route.id, k));
  }
  return out;
}

/** Every region within `reach` of `p` at time `t`: the areas (its own first), then the segments passing. */
export function regionsNear(sphere: SphereConfig, p: Vec2, reach: number, t: number): Region[] {
  return [...areasNear(sphere, p, reach).map(areaRegion), ...segmentsNear(sphere, p, reach, t)];
}

/** The areas (that exist) a box reaches into, `pad` around it. */
function areasTouching(sphere: SphereConfig, b: Bounds, pad: number): AreaCoord[] {
  const lo = areaOf(sphere, { x: b.x0 - pad, y: b.y0 - pad });
  const hi = areaOf(sphere, { x: b.x1 + pad, y: b.y1 + pad });
  const out: AreaCoord[] = [];
  for (let y = lo.y; y <= hi.y; y++) for (let x = lo.x; x <= hi.x; x++) if (areaExists(sphere, { x, y })) out.push({ x, y });
  return out;
}

/** Are two boxes within the margin of each other? */
const nearBox = (a: Bounds, b: Bounds) => a.x0 < b.x1 + AREA.margin && a.x1 > b.x0 - AREA.margin && a.y0 < b.y1 + AREA.margin && a.y1 > b.y0 - AREA.margin;

/**
 * A region's neighbours at time `t` (see AREA.margin): for an area, the
 * areas round it and every segment (of any stream) whose footprint is within
 * the margin of it; for a segment, the segments either side of it along its
 * belt, every other stream's segment whose footprint is within the margin of
 * its own (beside it on a shared stretch, or passing), and every area within
 * the margin of its footprint. Ghosts, hits, hurts and tile edits go to
 * whoever's a neighbour this tick.
 */
export function regionNeighbours(sphere: SphereConfig, r: Region, t: number): Region[] {
  const streams = generateGlobal(sphere).streams;
  if (r.kind === "area") {
    const out: Region[] = neighbourAreas(sphere, r.area).map(areaRegion);
    const b = areaBounds(sphere, r.area);
    for (const route of streams) for (let k = 0; k < route.segments; k++) if (nearBox(segmentBounds(sphere, route.id, k, t), b)) out.push(segmentRegion(route.id, k));
    return out;
  }
  const n = streams[r.stream]!.segments;
  const out: Region[] = [];
  if (n > 1) out.push(segmentRegion(r.stream, (r.k + n - 1) % n));
  if (n > 2) out.push(segmentRegion(r.stream, (r.k + 1) % n));
  const mine = segmentBounds(sphere, r.stream, r.k, t);
  for (const route of streams) {
    if (route.id === r.stream) continue;
    for (let k = 0; k < route.segments; k++) if (nearBox(segmentBounds(sphere, route.id, k, t), mine)) out.push(segmentRegion(route.id, k));
  }
  for (const a of areasTouching(sphere, mine, AREA.margin)) out.push(areaRegion(a));
  return out;
}
